import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import classNames from 'classnames';
import React, { useRef, useState, useCallback } from 'react';
import OverlayTrigger from 'react-bootstrap/lib/OverlayTrigger';
import Popover from 'react-bootstrap/lib/Popover';
import { useIntl } from 'react-intl';
import DateRangeFilter from './filters/DateRangeFilter';
import ItemsFilter from './filters/ItemsFilter';
import NumberFilter from './filters/NumberFilter';
import SearchFilter from './filters/SearchFilter';
import SelectFilter from './filters/SelectFilter';
import UserFilter from './filters/UserFilter';
import messages from './filters/filterMessages';
const MultiFilterColumnHeader = ({ filters, sortKey, onSort, sortDescFirst, onFilterMultiple, searchParams = {}, className, title, id, visible = true, style, badge = undefined, documentation, }) => {
    const intl = useIntl();
    const target = useRef(null);
    const overlayTriggerRef = useRef(null);
    // State to track which filters have been changed by the user
    // We only send changed filters to avoid clearing unchanged ones
    const [changedFilters, setChangedFilters] = useState({});
    if (!visible)
        return null;
    // Check if any filter is currently active
    const hasActiveFilters = filters.some(f => {
        if (f.type === 'search') {
            return searchParams.search && searchParams.search[f.key] !== undefined;
        }
        if (f.type === 'select') {
            return searchParams.select && searchParams.select[f.key] !== undefined;
        }
        if (f.type === 'items' || f.type === 'user') {
            return (searchParams.filter &&
                searchParams.filter[f.key] &&
                searchParams.filter[f.key].items &&
                searchParams.filter[f.key].items.length > 0);
        }
        if (f.type === 'number') {
            return (searchParams.filter &&
                searchParams.filter[f.key] &&
                (searchParams.filter[f.key].lesser != null || searchParams.filter[f.key].greater != null));
        }
        if (f.type === 'dateRange') {
            return (searchParams.filter &&
                searchParams.filter[f.key] &&
                (searchParams.filter[f.key].from != null || searchParams.filter[f.key].until != null));
        }
        return false;
    });
    // Handler for when a filter value changes
    const handleFilterChange = useCallback((key, value) => {
        setChangedFilters(prev => (Object.assign(Object.assign({}, prev), { [key]: value })));
    }, []);
    const applyAllFilters = useCallback(() => {
        if (onFilterMultiple && Object.keys(changedFilters).length > 0) {
            const changes = Object.entries(changedFilters).map(([key, value]) => ({
                property: key,
                filterRequest: value,
            }));
            onFilterMultiple(changes);
            setChangedFilters({});
        }
        if (overlayTriggerRef.current) {
            overlayTriggerRef.current.hide();
        }
    }, [onFilterMultiple, changedFilters]);
    const clearAllFilters = useCallback(() => {
        if (onFilterMultiple) {
            const changes = filters
                .filter(f => {
                if (f.type === 'number') {
                    return (searchParams.filter &&
                        searchParams.filter[f.key] &&
                        (searchParams.filter[f.key].lesser != null ||
                            searchParams.filter[f.key].greater != null));
                }
                if (f.type === 'dateRange') {
                    return (searchParams.filter &&
                        searchParams.filter[f.key] &&
                        (searchParams.filter[f.key].from != null || searchParams.filter[f.key].until != null));
                }
                return (searchParams.filter &&
                    searchParams.filter[f.key] &&
                    searchParams.filter[f.key].items &&
                    searchParams.filter[f.key].items.length > 0);
            })
                .map(f => {
                if (f.type === 'number') {
                    return { property: f.key, filterRequest: { lesser: null, greater: null } };
                }
                if (f.type === 'dateRange') {
                    return { property: f.key, filterRequest: { from: null, until: null } };
                }
                return { property: f.key, filterRequest: { items: [] } };
            });
            if (changes.length > 0) {
                onFilterMultiple(changes);
            }
            setChangedFilters({});
        }
        if (overlayTriggerRef.current) {
            overlayTriggerRef.current.hide();
        }
    }, [onFilterMultiple, filters, searchParams]);
    // Render a single filter component
    const getFilterComponent = (filterConfig, index) => {
        const isSearchFiltered = searchParams.search && searchParams.search[filterConfig.key] !== undefined;
        const isSelectFiltered = searchParams.select && searchParams.select[filterConfig.key] !== undefined;
        const isItemsFiltered = searchParams.filter &&
            searchParams.filter[filterConfig.key] &&
            searchParams.filter[filterConfig.key].items &&
            searchParams.filter[filterConfig.key].items.length > 0;
        let filterComponent;
        // All filters use the handleFilterChange callback
        // Individual filter buttons are hidden (hideFilterButton={true})
        const filterCallback = (value) => handleFilterChange(filterConfig.key, value);
        switch (filterConfig.type) {
            case 'search':
                filterComponent = (_jsx(SearchFilter, { initialValue: searchParams.search && searchParams.search[filterConfig.key], onFilter: filterCallback, isFiltered: isSearchFiltered, intl: intl }));
                break;
            case 'items':
                filterComponent = (_jsx(ItemsFilter, { items: filterConfig.items || [], renderItem: filterConfig.renderItem, initialFilteredItems: searchParams.filter &&
                        searchParams.filter[filterConfig.key] &&
                        searchParams.filter[filterConfig.key].items, onFilter: filterCallback, isFiltered: isItemsFiltered, intl: intl, hideFilterButton: true }));
                break;
            case 'select':
                filterComponent = (_jsx(SelectFilter, { initialValue: searchParams.select && searchParams.select[filterConfig.key], onFilter: filterCallback, loadFunc: filterConfig.loadFunc, isFiltered: isSelectFiltered, intl: intl }));
                break;
            case 'user':
                filterComponent = (_jsx(UserFilter, { initialValue: searchParams.filter &&
                        searchParams.filter[filterConfig.key] &&
                        searchParams.filter[filterConfig.key].items &&
                        searchParams.filter[filterConfig.key].items[0], onFilter: filterCallback, isFiltered: isItemsFiltered, intl: intl, withUnassigned: filterConfig.withUnassigned !== false, hideFilterButton: true }));
                break;
            case 'dateRange':
                filterComponent = (_jsx(DateRangeFilter, { intl: intl, initialValue: searchParams.filter && searchParams.filter[filterConfig.key], onFilter: filterCallback }));
                break;
            case 'number': {
                const isNumberFiltered = searchParams.filter &&
                    searchParams.filter[filterConfig.key] &&
                    (searchParams.filter[filterConfig.key].lesser != null ||
                        searchParams.filter[filterConfig.key].greater != null);
                filterComponent = (_jsx(NumberFilter, { initialValue: searchParams.filter && searchParams.filter[filterConfig.key], onFilter: filterCallback, isFiltered: isNumberFiltered, intl: intl, hideFilterButton: true }));
                break;
            }
            default:
                return null;
        }
        return (_jsxs(React.Fragment, { children: [index > 0 && filterConfig.type !== 'dateRange' && (_jsx("div", Object.assign({ style: { marginTop: '10px' } }, { children: _jsx("hr", { style: { margin: '0px' }, className: "dark-line" }) }))), filterConfig.type === 'user' || filterConfig.type === 'dateRange' ? (_jsx("div", Object.assign({ style: { marginTop: '12px' } }, { children: filterComponent }))) : (filterComponent)] }, filterConfig.key));
    };
    const sortClassName = classNames('fa margin10R', {
        clickable: sortKey,
        'fa-caret-down': sortKey && searchParams.desc,
        'fa-caret-up': sortKey && !searchParams.desc,
    });
    return (_jsxs("th", Object.assign({ className: classNames(className, 'sticky-header'), id: id, style: style, ref: target }, { children: [_jsxs("span", Object.assign({ className: classNames({ clickable: onSort }), onClick: () => onSort && onSort(sortKey, sortDescFirst) }, { children: [!!sortKey && sortKey === searchParams.sortKey && _jsx("i", { className: sortClassName }), title] })), badge && _jsx("br", {}), badge, filters.length > 0 && (_jsx(OverlayTrigger, Object.assign({ ref: overlayTriggerRef, trigger: "click", rootClose: true, placement: "bottom", overlay: _jsxs(Popover, Object.assign({ id: `multiFilterPopover-${filters.map(f => f.key).join('-')}`, style: { minWidth: 220, maxWidth: 300 } }, { children: [_jsx("button", Object.assign({ type: "button", className: "btn btn-inverse btn-block btn-sm margin10B", onClick: applyAllFilters }, { children: intl.formatMessage(messages.Filter) })), hasActiveFilters && (_jsxs("button", Object.assign({ type: "button", className: "btn btn-default btn-sm margin10B", onClick: clearAllFilters }, { children: [_jsx("i", { className: "fa fa-times" }), "\u00A0", _jsx("span", { children: intl.formatMessage(messages.ClearFilter) })] }))), filters.map((f, index) => getFilterComponent(f, index))] })), container: () => target.current }, { children: _jsx("i", { className: classNames('fa fa-filter margin10L clickable filter-icon', {
                        red: hasActiveFilters,
                    }) }) })))] })));
};
export default MultiFilterColumnHeader;
